// Reusable chat surface used by Community groups (Phase A) and private DMs (Phase B).
// Loads messages via REST polling every 5 seconds and supports the full WhatsApp/Telegram
// feature set: reply, edit, delete, copy, forward, react, mention, search, pin, voice notes,
// images, PDFs, mute, report.

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage, useAuth, API } from "@/App";
import { communityApi, attachmentUrl } from "@/lib/community";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Send, Image as ImageIcon, Paperclip, Mic, Square, X, Reply, Copy,
  Edit2, Trash2, Pin, PinOff, Flag, Smile, Search, BellOff, Bell, ArrowLeft,
  CornerUpRight, MoreVertical, Play, Pause, FileText, ChevronUp,
} from "lucide-react";

const REACTIONS = ["❤️", "👍", "😂", "😮", "😢", "🙏", "🔥"];

const fmtTime = (iso, lang) => new Date(iso).toLocaleTimeString(lang === "ar" ? "ar-EG" : "en-US", { hour: "2-digit", minute: "2-digit" });

// ─── Voice recorder hook ───
function useVoiceRecorder({ maxMs = 60_000, onStop }) {
  const recRef     = useRef(null);
  const chunksRef  = useRef([]);
  const timerRef   = useRef(null);
  const [state, setState] = useState({ recording: false, ms: 0 });

  // Capability detection — voice recording requires a secure context (HTTPS),
  // navigator.mediaDevices.getUserMedia, and the MediaRecorder API.
  const supported = typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function"
    && typeof window.MediaRecorder !== "undefined"
    && (window.isSecureContext || window.location.hostname === "localhost");

  const start = async () => {
    if (!supported) {
      toast.error("التسجيل الصوتي غير متاح في هذا المتصفح. جرّب Chrome على Desktop أو Safari على iOS.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec  = new MediaRecorder(stream, { mimeType: mime });
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const ms = state.ms;
        setState({ recording: false, ms: 0 });
        clearInterval(timerRef.current);
        onStop({ blob, ms });
      };
      rec.start();
      let elapsed = 0;
      setState({ recording: true, ms: 0 });
      timerRef.current = setInterval(() => {
        elapsed += 200;
        setState({ recording: true, ms: elapsed });
        if (elapsed >= maxMs) stopAndSend();
      }, 200);
    } catch (e) {
      // Surface a specific, user-facing message based on the actual failure mode.
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error("تم رفض إذن الميكروفون. افتح إعدادات المتصفح واسمح بالوصول للميكروفون ثم حاول مرة أخرى.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        toast.error("لم نجد ميكروفوناً متاحاً على جهازك.");
      } else if (name === "NotReadableError") {
        toast.error("الميكروفون مشغول بتطبيق آخر. أغلق التطبيقات الأخرى ثم حاول.");
      } else {
        toast.error("تعذّر بدء التسجيل الصوتي. تأكد من السماح بإذن الميكروفون.");
      }
    }
  };

  const stopAndSend = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };
  const cancel = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.onstop = null;
      try { recRef.current.stop(); } catch { /* recorder already stopped */ }
      recRef.current.stream?.getTracks?.().forEach((tr) => tr.stop());
    }
    clearInterval(timerRef.current);
    setState({ recording: false, ms: 0 });
  };
  return { start, stopAndSend, cancel, state, supported };
}

// ─── Voice message player ───
function VoiceBubble({ path }) {
  const url = attachmentUrl(path);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play();  setPlaying(true); }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <button onClick={toggle} className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-md" data-testid="voice-play-btn">
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="flex items-center gap-1">
        {[...Array(20)].map((_, i) => (
          <span key={i} className="w-0.5 h-3 bg-white/70 rounded-full" style={{ height: `${6 + ((i*3 + 5) % 12)}px` }} />
        ))}
      </div>
      <audio ref={audioRef} src={url} preload="metadata" onEnded={() => setPlaying(false)} className="hidden" />
    </div>
  );
}

// ─── Single message bubble ───
function MessageBubble({ msg, me, onReply, onEdit, onDelete, onReact, onCopy, onPin, onForward, onReport, language, t }) {
  const mine    = msg.user_id === me?.user_id;
  const deleted = !!msg.deleted_at;
  const [menuOpen, setMenuOpen]       = useState(false);
  const [reactPickerOpen, setRP]      = useState(false);

  if (deleted) {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`} data-testid={`msg-deleted-${msg.message_id}`}>
        <div className="bg-slate-800/40 border border-slate-700 rounded-2xl px-4 py-2 text-xs text-slate-500 italic">
          {t("تم حذف هذه الرسالة", "This message was deleted")}
        </div>
      </div>
    );
  }

  const bubbleCls = mine
    ? "bg-emerald-600 text-white rounded-2xl rounded-br-sm"
    : "bg-slate-800 text-white rounded-2xl rounded-bl-sm";

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"} mb-2`} data-testid={`msg-${msg.message_id}`} dir="auto">
      <div className="max-w-[80%] lg:max-w-[60%] relative">
        {/* Sender */}
        {!mine && (
          <div className="text-[10px] font-semibold text-yellow-400 mb-0.5 px-1">{msg.user_name || "—"}</div>
        )}
        {/* Pin badge */}
        {msg.pinned_at && (
          <div className="text-[10px] text-yellow-300 inline-flex items-center gap-1 mb-0.5 px-1"><Pin size={10} />{t("مثبتة", "Pinned")}</div>
        )}
        <div className={`${bubbleCls} px-3 py-2 shadow-sm relative`}>
          {/* Reply preview */}
          {msg.reply_to && (
            <div className="text-[10px] bg-black/15 border-l-2 border-white/40 px-2 py-1 mb-1 rounded">
              <div className="font-semibold opacity-90">{msg.reply_to.user_name}</div>
              <div className="opacity-75 truncate">{msg.reply_to.body || "…"}</div>
            </div>
          )}
          {/* Image attachment */}
          {msg.type === "image" && msg.attachment && (
            <img src={attachmentUrl(msg.attachment.path)} alt="" className="max-w-full max-h-72 rounded-lg cursor-pointer"
                 onClick={() => window.open(attachmentUrl(msg.attachment.path), "_blank")} />
          )}
          {/* Voice */}
          {msg.type === "voice" && msg.attachment && <VoiceBubble path={msg.attachment.path} />}
          {/* File / PDF */}
          {msg.type === "file" && msg.attachment && (
            <a href={attachmentUrl(msg.attachment.path)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-black/15 rounded-lg px-2 py-1.5 text-xs hover:bg-black/25">
              <FileText size={14} />
              <span className="truncate max-w-[180px]">{msg.attachment.name || "file"}</span>
            </a>
          )}
          {/* Text body */}
          {msg.body && <div className="text-sm whitespace-pre-wrap break-words">{msg.body}</div>}
          {/* Meta */}
          <div className="flex items-center gap-1 mt-0.5 text-[10px] opacity-70 justify-end">
            {msg.edited_at && <span>{t("معدّلة", "edited")}</span>}
            <span>{fmtTime(msg.created_at, language)}</span>
          </div>
          {/* Reactions */}
          {msg.reactions && Object.keys(msg.reactions).length > 0 && (
            <div className="absolute -bottom-3 left-2 flex items-center gap-0.5 bg-slate-900 border border-slate-700 rounded-full px-1.5 py-0.5 shadow text-[11px]" data-testid={`msg-reactions-${msg.message_id}`}>
              {Object.entries(msg.reactions).map(([e, users]) => (
                <button key={e} onClick={() => onReact(msg, e)} className="hover:scale-110 transition" title={`${users.length}`}>
                  <span>{e}</span><span className="opacity-70 mr-0.5">{users.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Hover actions */}
        <div className={`absolute -top-3 ${mine ? "left-0" : "right-0"} opacity-0 group-hover:opacity-100 transition flex items-center gap-0.5 bg-slate-900 border border-slate-700 rounded-full px-1.5 py-1 shadow z-10`}>
          <button onClick={() => setRP((v) => !v)} className="text-slate-300 hover:text-yellow-400 p-1" title="React" data-testid={`msg-react-btn-${msg.message_id}`}><Smile size={13} /></button>
          <button onClick={() => onReply(msg)}    className="text-slate-300 hover:text-yellow-400 p-1" title="Reply" data-testid={`msg-reply-btn-${msg.message_id}`}><Reply size={13} /></button>
          <button onClick={() => onCopy(msg)}     className="text-slate-300 hover:text-yellow-400 p-1" title="Copy"  data-testid={`msg-copy-btn-${msg.message_id}`}><Copy size={13} /></button>
          <button onClick={() => onForward(msg)}  className="text-slate-300 hover:text-yellow-400 p-1" title="Forward"data-testid={`msg-forward-btn-${msg.message_id}`}><CornerUpRight size={13} /></button>
          <button onClick={() => setMenuOpen(v => !v)} className="text-slate-300 hover:text-yellow-400 p-1" data-testid={`msg-more-btn-${msg.message_id}`}><MoreVertical size={13} /></button>
        </div>
        {/* Quick reaction picker */}
        {reactPickerOpen && (
          <div className={`absolute -top-12 ${mine ? "left-0" : "right-0"} bg-slate-900 border border-slate-700 rounded-full px-2 py-1 flex gap-1 shadow z-20`} data-testid={`msg-react-picker-${msg.message_id}`}>
            {REACTIONS.map((e) => (
              <button key={e} onClick={() => { onReact(msg, e); setRP(false); }} className="hover:scale-125 transition text-lg" data-testid={`msg-react-${msg.message_id}-${e}`}>{e}</button>
            ))}
          </div>
        )}
        {/* Action menu */}
        {menuOpen && (
          <div className={`absolute ${mine ? "right-0" : "left-0"} top-6 bg-slate-900 border border-slate-700 rounded-xl py-1 shadow-xl z-30 min-w-[140px]`} data-testid={`msg-menu-${msg.message_id}`}>
            <button onClick={() => { onPin(msg); setMenuOpen(false); }} className="w-full text-right text-xs px-3 py-2 hover:bg-slate-800 inline-flex items-center gap-2 text-slate-200">
              {msg.pinned_at ? <PinOff size={12} /> : <Pin size={12} />} {msg.pinned_at ? t("إلغاء التثبيت", "Unpin") : t("تثبيت", "Pin")}
            </button>
            {mine && msg.type === "text" && (
              <button onClick={() => { onEdit(msg); setMenuOpen(false); }} className="w-full text-right text-xs px-3 py-2 hover:bg-slate-800 inline-flex items-center gap-2 text-slate-200" data-testid={`msg-edit-${msg.message_id}`}>
                <Edit2 size={12} /> {t("تعديل", "Edit")}
              </button>
            )}
            {mine && (
              <button onClick={() => { onDelete(msg); setMenuOpen(false); }} className="w-full text-right text-xs px-3 py-2 hover:bg-red-500/20 text-red-300 inline-flex items-center gap-2" data-testid={`msg-delete-${msg.message_id}`}>
                <Trash2 size={12} /> {t("حذف", "Delete")}
              </button>
            )}
            {!mine && (
              <button onClick={() => { onReport(msg); setMenuOpen(false); }} className="w-full text-right text-xs px-3 py-2 hover:bg-red-500/20 text-red-300 inline-flex items-center gap-2" data-testid={`msg-report-${msg.message_id}`}>
                <Flag size={12} /> {t("تبليغ", "Report")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Main Chat Surface
//
// Used by both Public Groups (Phase A) and Private DMs (Phase B). The `mode`
// prop selects which set of API endpoints to talk to:
//   mode="group" → slug is a group slug → /community/groups/{slug}/...
//   mode="dm"    → slug is a DM thread id → /dm/threads/{slug}/...
// ═══════════════════════════════════════════════════════════════════════
export default function ChatSurface({ slug, mode = "group", header }) {
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta]           = useState(null);
  const [messages, setMessages]   = useState([]);
  const [draft, setDraft]         = useState("");
  const [reply, setReply]         = useState(null);    // {message_id, body, user_name}
  const [editing, setEditing]     = useState(null);    // message being edited
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ]     = useState("");
  const [searchResults, setSR]    = useState(null);
  const [sending, setSending]     = useState(false);
  const scrollRef = useRef(null);

  // Adapter: route every API call through the right endpoint family.
  const api = mode === "dm" ? {
    detail:  () => Promise.resolve(header || { ...meta }),
    list:    () => communityApi.dmList(slug, token),
    send:    (b) => communityApi.dmSend(slug, b, token),
    read:    () => communityApi.dmRead(slug, token),
    search:  null,        // DM search not in this MVP
    mute:    null,
  } : {
    detail:  () => communityApi.groupDetail(slug, token),
    list:    () => communityApi.list(slug, token),
    send:    (b) => communityApi.send(slug, b, token),
    read:    () => communityApi.markRead(slug, token),
    search:  (q) => communityApi.search(slug, q, token),
    mute:    () => communityApi.toggleMute(slug, token),
  };

  // Build a small id→message lookup so we can render reply previews.
  const enrich = useCallback((list) => {
    const byId = Object.fromEntries(list.map(m => [m.message_id, m]));
    return list.map(m => ({ ...m, reply_to: m.reply_to_id ? byId[m.reply_to_id] : null }));
  }, []);

  // Load messages + maintain a realtime WebSocket connection.
  //
  // Behavior:
  //  - One WebSocket per ChatSurface instance, opened against /api/ws/chat.
  //  - For community groups we send {op:"subscribe", group:slug} so the server
  //    only fans out messages for groups we're actively viewing.
  //  - On incoming "group.new" / "dm.new" frames matching this surface we
  //    append the message to local state immediately.
  //  - REST polling is kept as a fallback at a slower 15s cadence so the UI
  //    still updates even if the WS disconnects (mobile sleep, proxy timeout).
  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    let mounted = true;
    const load = async () => {
      try {
        const [m, ms] = await Promise.all([api.detail(), api.list()]);
        if (!mounted) return;
        setMeta(m);
        setMessages(enrich(ms));
        await api.read().catch(() => {});
      } catch { /* silent — polling will retry */ }
    };
    load();

    // WebSocket realtime ────────────────────────────────────────────────
    let ws = null;
    let reconnect = null;
    const apiBase = API.replace(/\/api$/, "");
    const wsBase  = apiBase.replace(/^http/, "ws");
    const connect = () => {
      if (!mounted) return;
      try {
        ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`);
      } catch { return; }
      ws.onopen = () => {
        if (mode === "community" && slug) {
          try { ws.send(JSON.stringify({ op: "subscribe", group: slug })); } catch { /* noop */ }
        }
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          // Community group push
          if (data?.type === "group.new" && mode === "community" && data.group_slug === slug) {
            setMessages((prev) => prev.some((m) => m.message_id === data.message.message_id)
              ? prev
              : enrich([...prev, data.message]));
          }
          // Direct message push — apply when this surface IS the matching DM thread
          if (data?.type === "dm.new" && mode === "dm" && data.conversation_id === slug) {
            setMessages((prev) => prev.some((m) => m.message_id === data.message.message_id)
              ? prev
              : enrich([...prev, data.message]));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!mounted) return;
        reconnect = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws && ws.close(); } catch { /* noop */ } };
    };
    connect();

    // REST polling fallback (slow cadence — WS handles the hot path)
    const iv = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(iv);
      if (reconnect) clearTimeout(reconnect);
      if (ws) {
        try { if (mode === "community" && slug) ws.send(JSON.stringify({ op: "unsubscribe", group: slug })); } catch { /* noop */ }
        try { ws.close(); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, navigate, enrich, mode]);

  // Autoscroll on new message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const sendText = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      if (editing) {
        await communityApi.edit(editing.message_id, { type: "text", body: draft }, token);
        setEditing(null);
      } else {
        await api.send({ type: "text", body: draft, reply_to_id: reply?.message_id || null });
      }
      setDraft(""); setReply(null);
      const ms = await api.list();
      setMessages(enrich(ms));
    } catch {
      toast.error(t("تعذّر إرسال الرسالة", "Could not send message"));
    } finally { setSending(false); }
  };

  const uploadAndSend = async (file, type) => {
    if (!file) return;
    setSending(true);
    try {
      const att = await communityApi.upload(file, token);
      await api.send({ type, body: "", attachment: { path: att.path, content_type: att.content_type, size: att.size, name: att.name } });
      const ms = await api.list();
      setMessages(enrich(ms));
    } catch {
      toast.error(t("تعذّر رفع الملف", "Could not upload file"));
    } finally { setSending(false); }
  };

  const voice = useVoiceRecorder({
    maxMs: 60_000,
    onStop: async ({ blob, ms }) => {
      const file = new File([blob], `voice_${Date.now()}.webm`, { type: "audio/webm" });
      try {
        const att = await communityApi.upload(file, token);
        await api.send({ type: "voice", body: "", attachment: { path: att.path, content_type: att.content_type, size: att.size, name: file.name, duration_ms: ms } });
        const list = await api.list();
        setMessages(enrich(list));
      } catch { toast.error(t("تعذّر إرسال الرسالة الصوتية", "Could not send voice message")); }
    },
  });

  // Handlers
  const onReply  = (m) => { setReply(m); setEditing(null); };
  const onEdit   = (m) => { setEditing(m); setReply(null); setDraft(m.body || ""); };
  const onDelete = async (m) => {
    if (!window.confirm(t("هل تريد حذف هذه الرسالة؟", "Delete this message?"))) return;
    await communityApi.remove(m.message_id, token);
    setMessages((xs) => xs.map(x => x.message_id === m.message_id ? { ...x, deleted_at: new Date().toISOString(), body: "", attachment: null } : x));
  };
  const onReact  = async (m, emoji) => {
    const res = await communityApi.react(m.message_id, emoji, token);
    setMessages((xs) => xs.map(x => x.message_id === m.message_id ? { ...x, reactions: res.reactions } : x));
  };
  const onCopy   = (m) => { navigator.clipboard.writeText(m.body || ""); toast.success(t("تم النسخ", "Copied")); };
  const onPin    = async (m) => {
    const res = await communityApi.pin(m.message_id, token);
    setMessages((xs) => xs.map(x => x.message_id === m.message_id ? { ...x, pinned_at: res.pinned ? new Date().toISOString() : null } : x));
  };
  const onForward = (m) => {
    const url = `${window.location.origin}/community/${slug}?msg=${m.message_id}`;
    navigator.clipboard.writeText(`${m.body || ""}\n\n${url}`);
    toast.success(t("تم نسخ الرسالة — الصقها في محادثة أخرى", "Message copied — paste it in any other chat"));
  };
  const onReport = async (m) => {
    const reason = window.prompt(t("سبب البلاغ:", "Reason for the report:")) || "";
    if (!reason.trim()) return;
    await communityApi.report(m.message_id, reason.trim(), token);
    toast.success(t("تم استلام بلاغك", "Your report has been received"));
  };

  const toggleMute = async () => {
    if (!api.mute) return;
    const r = await api.mute();
    setMeta((mm) => mm ? { ...mm, muted: r.muted } : mm);
    toast.success(r.muted ? t("تم كتم الإشعارات", "Notifications muted") : t("تم تفعيل الإشعارات", "Notifications on"));
  };

  const runSearch = async (q) => {
    setSearchQ(q);
    if (!q.trim() || !api.search) { setSR(null); return; }
    const r = await api.search(q);
    setSR(r);
  };

  if (!meta) {
    return <div className="flex items-center justify-center h-[60vh]"><div className="text-slate-400">{t("جارٍ التحميل…", "Loading…")}</div></div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] lg:h-[calc(100vh-100px)] -mx-4 lg:mx-0 lg:rounded-2xl overflow-hidden bg-slate-950 lg:border lg:border-slate-800" data-testid="chat-surface">
      {/* HEADER */}
      <div className="flex-shrink-0 flex items-center gap-3 px-3 lg:px-4 py-3 bg-slate-900 border-b border-slate-800">
        <button onClick={() => navigate(mode === "dm" ? "/messages" : "/community")} className="p-1.5 text-slate-300 hover:text-white" data-testid="chat-back-btn">
          <ArrowLeft size={18} />
        </button>
        {mode === "dm" ? (
          <>
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-base bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-bold flex-shrink-0">
              {(meta?.other_name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{meta?.other_name || ""}</div>
              <div className="text-[10px] text-slate-400 truncate">{meta?.other_username ? `@${meta.other_username}` : t("محادثة خاصة", "Private chat")}</div>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: `linear-gradient(135deg, ${meta.accent}, #0f172a)` }}>
              {meta.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{language === "ar" ? meta.name_ar : meta.name_en}</div>
              <div className="text-[10px] text-slate-400 truncate">
                {meta.members_count.toLocaleString(language === "ar" ? "ar-EG" : "en-US")} {t("عضو", "members")}
              </div>
            </div>
          </>
        )}
        {api.search && <button onClick={() => setSearchOpen((v) => !v)} className="p-1.5 text-slate-300 hover:text-white" data-testid="chat-search-toggle"><Search size={16} /></button>}
        {api.mute && <button onClick={toggleMute} className="p-1.5 text-slate-300 hover:text-white" data-testid="chat-mute-toggle">{meta.muted ? <BellOff size={16} /> : <Bell size={16} />}</button>}
      </div>

      {/* Pinned messages strip */}
      {mode !== "dm" && meta?.pinned && meta.pinned.length > 0 && (
        <div className="flex-shrink-0 bg-yellow-500/10 border-b border-yellow-500/20 px-3 py-2 text-xs text-yellow-200 flex items-center gap-2" data-testid="chat-pinned-strip">
          <Pin size={12} />
          <span className="truncate flex-1">{meta.pinned[0].body || t("(محتوى مرئي)", "(visual content)")}</span>
        </div>
      )}

      {/* Search panel */}
      {searchOpen && (
        <div className="flex-shrink-0 px-3 py-2 bg-slate-900 border-b border-slate-800" data-testid="chat-search-panel">
          <Input value={searchQ} onChange={(e) => runSearch(e.target.value)} placeholder={t("ابحث في الرسائل…", "Search messages…")} className="bg-slate-800 text-white border-slate-700" data-testid="chat-search-input" />
        </div>
      )}

      {/* MESSAGES (or search results) */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 lg:px-4 py-3 bg-slate-950">
        {searchResults !== null ? (
          searchResults.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-10">{t("لا توجد نتائج مطابقة", "No matching messages")}</div>
          ) : searchResults.map((m) => (
            <MessageBubble key={m.message_id} msg={m} me={user} {...{onReply, onEdit, onDelete, onReact, onCopy, onPin, onForward, onReport, language, t}} />
          ))
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-20">{t("لا توجد رسائل بعد — كن أول من يكتب 👋", "No messages yet — be the first to say hi 👋")}</div>
        ) : messages.map((m) => (
          <MessageBubble key={m.message_id} msg={m} me={user} {...{onReply, onEdit, onDelete, onReact, onCopy, onPin, onForward, onReport, language, t}} />
        ))}
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 bg-slate-900 border-t border-slate-800 px-3 py-2">
        {/* Reply / editing preview */}
        {(reply || editing) && (
          <div className="flex items-center gap-2 bg-slate-800 border-l-2 border-yellow-500 px-2 py-1.5 mb-2 rounded-lg" data-testid="chat-composer-preview">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-yellow-300 font-semibold">{editing ? t("تعديل رسالتك", "Editing your message") : `${t("الرد على", "Replying to")} ${reply?.user_name || ""}`}</div>
              <div className="text-xs text-slate-300 truncate">{editing?.body || reply?.body || "(media)"}</div>
            </div>
            <button onClick={() => { setReply(null); setEditing(null); setDraft(""); }} className="p-1 text-slate-400 hover:text-white"><X size={14} /></button>
          </div>
        )}

        {/* Voice recording state */}
        {voice.state.recording ? (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2" data-testid="chat-voice-recording">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-red-300 font-mono">{Math.floor(voice.state.ms / 1000)}s / 60s</span>
            <span className="flex-1 text-xs text-slate-300">{t("جارٍ التسجيل…", "Recording…")}</span>
            <button onClick={voice.cancel} className="p-1.5 text-slate-400 hover:text-white" data-testid="chat-voice-cancel"><X size={16} /></button>
            <button onClick={voice.stopAndSend} className="p-1.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600" data-testid="chat-voice-send"><Send size={14} /></button>
          </div>
        ) : (
          <div className="flex items-end gap-1.5">
            {/* Image */}
            <label className="cursor-pointer p-2 text-slate-400 hover:text-yellow-400" data-testid="chat-image-upload" title={t("صورة", "Image")}>
              <ImageIcon size={18} />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAndSend(e.target.files?.[0], "image")} />
            </label>
            {/* File */}
            <label className="cursor-pointer p-2 text-slate-400 hover:text-yellow-400" data-testid="chat-file-upload" title={t("ملف", "File")}>
              <Paperclip size={18} />
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => uploadAndSend(e.target.files?.[0], "file")} />
            </label>
            {/* Text input */}
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              placeholder={t("اكتب رسالة…", "Type a message…")}
              rows={1}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-2xl px-3 py-2 text-sm text-white resize-none max-h-32 outline-none focus:border-yellow-500"
              data-testid="chat-input"
            />
            {/* Send / Voice */}
            {draft.trim() ? (
              <Button onClick={sendText} disabled={sending} className="btn-primary rounded-full w-9 h-9 p-0" data-testid="chat-send-btn">
                <Send size={14} />
              </Button>
            ) : (
              <button
                onClick={voice.start}
                title={voice.supported ? "تسجيل رسالة صوتية" : "التسجيل الصوتي غير متاح في هذا المتصفح"}
                className={`w-9 h-9 rounded-full text-white flex items-center justify-center ${voice.supported ? "bg-emerald-500 hover:bg-emerald-600" : "bg-slate-600 hover:bg-slate-500 opacity-70"}`}
                data-testid="chat-voice-start"
              >
                <Mic size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
